"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@hackclub/icons";
import type { ComponentProps } from "react";
import { SPARK_FAMILIES, type SparkFamilyLabel } from "./spark-families";

type Glyph = ComponentProps<typeof Icon>["glyph"];

/** Port of the "Add a New Spark" form from kindness_sparks_frontend_v2
 *  (src/pages/AddNewSpark.jsx + addNewSpark.css), as a popup instead of its
 *  own page. Saving posts to /api/spark-factory/draft, which forwards to the
 *  Kindness Sparks spark factory (name → idea, category → family_hint).
 *  Styles are scoped under .sbx-ans so they can't leak into the rest of the
 *  Spark Box page. */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a draft is created, with its name. The popup then closes;
   *  the page shows the confirmation and refreshes its lists. */
  onSaved?: (name: string) => void;
}

export function AddSparkModal({ open, onClose, onSaved }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<SparkFamilyLabel>(SPARK_FAMILIES[0].label);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  function reset() {
    setName("");
    setCategory(SPARK_FAMILIES[0].label);
    setError("");
    setSaving(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please fill in the spark name.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/spark-factory/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idea: name.trim(), family_hint: category, smoke_test: true }),
      });
      const data = (await res.json().catch(() => ({}))) as { name?: string; error?: string };
      if (!res.ok) {
        setError(data.error || "Something went wrong saving your spark. Please try again.");
        return;
      }
      onSaved?.(data.name || name.trim());
      handleClose();
    } catch {
      setError("Something went wrong saving your spark. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="sbx-ans"
      aria-labelledby="sbx-ans-title"
      // Esc fires "cancel"; route it through handleClose so parent state stays in sync.
      onCancel={(e) => {
        e.preventDefault();
        handleClose();
      }}
    >
      <div className="sbx-ans__panel">
        <button type="button" className="sbx-ans__close" aria-label="Close" onClick={handleClose}>
          <Icon glyph="view-close" size={22} />
        </button>

        <div className="sbx-ans__header">
          <span className="sbx-ans__tag">New Spark</span>
          <h2 id="sbx-ans-title" className="sbx-ans__title">
            Add a New Spark
          </h2>
          <p className="sbx-ans__subtitle">
            Share a small idea that could brighten someone&apos;s day.
          </p>
        </div>

        <form className="sbx-ans__card" onSubmit={handleSubmit} noValidate>
          <label className="sbx-ans__field">
            <span className="sbx-ans__label">New Spark Name</span>
            <input
              className="sbx-ans__input"
              type="text"
              value={name}
              maxLength={80}
              placeholder="e.g. Leave a kind note for a neighbor"
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
            />
          </label>

          <fieldset className="sbx-ans__field sbx-ans__fieldset">
            <legend className="sbx-ans__label">Category</legend>
            <div className="sbx-ans__chips">
              {SPARK_FAMILIES.map((f) => (
                <label
                  key={f.key}
                  className={`sbx-ans__chip${category === f.label ? " sbx-ans__chip--on" : ""}`}
                >
                  <input
                    type="radio"
                    name="sbx-ans-category"
                    value={f.label}
                    checked={category === f.label}
                    onChange={() => setCategory(f.label)}
                  />
                  <Icon glyph={f.icon as Glyph} size={22} />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {error && (
            <div className="sbx-ans__error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="sbx-ans__submit" disabled={saving}>
            {saving ? (
              <>
                <span className="sbx-ans__spinner" aria-hidden="true" />
                Loading…
              </>
            ) : (
              "Save and Submit"
            )}
          </button>
        </form>
      </div>

      <style>{`
        .sbx-ans {
          /* Tailwind preflight zeroes margins, which strips the UA's
             margin: auto centring for modal dialogs — restore it. */
          position: fixed;
          inset: 0;
          margin: auto;
          height: fit-content;
          border: none;
          padding: 0;
          background: transparent;
          width: min(560px, calc(100vw - 32px));
          max-height: calc(100vh - 32px);
          overflow: visible;
          color: var(--sbx-ink);
        }
        .sbx-ans::backdrop {
          background: rgba(45, 42, 38, 0.45);
        }
        .sbx-ans[open] {
          animation: sbx-ans-in 0.2s ease both;
        }
        @keyframes sbx-ans-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sbx-ans *, .sbx-ans *::before, .sbx-ans *::after { box-sizing: border-box; }

        .sbx-ans__panel {
          position: relative;
          background: var(--sbx-paper);
          border-radius: 22px;
          padding: 22px 22px 20px;
          max-height: calc(100vh - 32px);
          overflow-y: auto;
          overflow-x: hidden;
          box-shadow: 0 20px 50px rgba(45, 42, 38, 0.25);
        }

        .sbx-ans__close {
          position: absolute;
          top: 14px;
          right: 14px;
          display: flex;
          padding: 4px;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: var(--sbx-ink-soft);
          cursor: pointer;
        }
        .sbx-ans__close:hover { background: var(--sbx-ember-tint); color: var(--sbx-ink); }

        .sbx-ans__header { text-align: center; margin-bottom: 14px; }
        .sbx-ans__tag {
          display: inline-block;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--sbx-ember-deep);
          background: var(--sbx-ember-tint);
          padding: 6px 14px;
          border-radius: 999px;
          margin-bottom: 8px;
        }
        .sbx-ans__title {
          font-size: clamp(1.35rem, 3vw, 1.75rem);
          font-weight: 800;
          letter-spacing: -0.5px;
          line-height: 1.15;
          margin: 0 0 4px;
        }
        .sbx-ans__subtitle {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--sbx-ink-soft);
          line-height: 1.6;
          margin: 0;
        }

        .sbx-ans__card {
          background: var(--sbx-paper-raised);
          border: 1px solid var(--sbx-line);
          border-radius: 18px;
          padding: 18px 18px;
          box-shadow: var(--sbx-shadow-card);
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .sbx-ans__field { display: flex; flex-direction: column; gap: 6px; }
        .sbx-ans__label { font-size: 13px; font-weight: 800; color: var(--sbx-ink); }

        .sbx-ans__input {
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          color: var(--sbx-ink);
          background: var(--sbx-paper);
          border: 1px solid var(--sbx-line);
          border-radius: 12px;
          padding: 10px 14px;
          outline: none;
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .sbx-ans__input::placeholder { color: var(--sbx-ink-faint); }
        .sbx-ans__input:focus {
          border-color: var(--sbx-peri);
          box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.18);
        }
        .sbx-ans__fieldset { border: none; margin: 0; padding: 0; min-width: 0; }
        .sbx-ans__fieldset .sbx-ans__label { padding: 0; margin-bottom: 8px; }
        .sbx-ans__chips {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
        }
        @media (max-width: 520px) {
          .sbx-ans__chips { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        .sbx-ans__chip {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 10px 4px 8px;
          border: 1px solid var(--sbx-line);
          border-radius: 12px;
          background: var(--sbx-paper);
          color: var(--sbx-ink-soft);
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
        }
        .sbx-ans__chip:hover { border-color: var(--sbx-ember); color: var(--sbx-ink); }
        .sbx-ans__chip--on {
          border-color: var(--sbx-ember-deep);
          background: var(--sbx-ember-tint);
          color: var(--sbx-ember-deep);
        }
        /* Native radio kept for keyboard and screen readers, visually hidden. */
        .sbx-ans__chip input {
          position: absolute;
          opacity: 0;
          width: 1px;
          height: 1px;
          pointer-events: none;
        }
        .sbx-ans__chip:has(input:focus-visible) {
          box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.25);
          border-color: var(--sbx-peri);
        }

        .sbx-ans__error {
          font-size: 13px;
          font-weight: 700;
          color: var(--sbx-ember-deep);
          background: var(--sbx-ember-tint);
          padding: 10px 14px;
          border-radius: 10px;
        }

        .sbx-ans__submit {
          align-self: center;
          display: inline-flex;
          align-items: center;
          padding: 12px 28px;
          border: none;
          border-radius: 999px;
          background: var(--sbx-ember);
          color: #fff;
          font: inherit;
          font-weight: 800;
          font-size: 14px;
          cursor: pointer;
          margin-top: 4px;
          box-shadow: var(--sbx-shadow-card);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .sbx-ans__submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: var(--sbx-shadow-soft);
        }
        .sbx-ans__submit:disabled { opacity: 0.7; cursor: default; }
        .sbx-ans__submit { gap: 8px; }
        .sbx-ans__spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.45);
          border-top-color: #fff;
          border-radius: 50%;
          animation: sbx-ans-spin 0.8s linear infinite;
        }
        @keyframes sbx-ans-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </dialog>
  );
}
